import React from 'react';
import Icon from '../../../components/AppIcon';
import ProgressIndicator from '../../../components/ui/ProgressIndicator';
import Button from '../../../components/ui/Button';


const ProgressSummary = ({ stats, achievements, streakInfo = null }) => {
  const currentStreak = streakInfo?.currentStreak ?? stats?.streakDays ?? 0;
  const longestStreak = streakInfo?.longestStreak ?? 0;
  
  const progressCards = [
    {
      id: 'streak',
      title: 'Current Streak',
      value: currentStreak,
      unit: 'days',
      icon: 'Flame',
      color: 'var(--color-success)',
      bgColor: 'bg-success/10',
      hoverBg: 'hover:bg-success/20',
      description: currentStreak === 0 ? 'Start today!' : 
                   currentStreak === 1 ? 'Great start!' : 
                   currentStreak < 7 ? 'Keep it going!' : 
                   currentStreak < 30 ? 'Amazing progress!': 'Incredible dedication!',
      subtitle: longestStreak > 0 ? `Best: ${longestStreak} days` : null
    },
    {
      id: 'memorized',
      title: 'Verses Memorized',
      value: stats?.totalMemorized,
      unit: 'verses',
      icon: 'BookMarked',
      color: 'var(--color-primary)',
      bgColor: 'bg-primary/10',
      hoverBg: 'hover:bg-primary/20',
      description: 'Total completed'
    },
    {
      id: 'weekly',
      title: 'This Week',
      value: stats?.weeklyProgress,
      unit: `/${stats?.weeklyGoal}`,
      icon: 'Target',
      color: 'var(--color-accent)',
      bgColor: 'bg-accent/10',
      hoverBg: 'hover:bg-accent/20',
      description: 'Weekly goal'
    },
    {
      id: 'time',
      title: 'Study Time',
      value: stats?.studyTimeWeek ?? stats?.studyTimeToday ?? 0,
      unit: 'min',
      icon: 'Clock',
      color: 'var(--color-warning)',
      bgColor: 'bg-warning/10',
      hoverBg: 'hover:bg-warning/20',
      description: 'This week'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Main Progress Indicator */}
      <div className="group bg-gradient-to-br from-card via-card to-primary/5 rounded-xl p-8 shadow-lg border border-border hover:shadow-xl hover:border-primary/30 hover:scale-[1.005] transition-all duration-300 ease-out">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl-custom font-heading font-bold text-foreground group-hover:text-primary transition-colors duration-300">Your Progress</h2>
          <Button
            variant="ghost"
            size="icon"
            title="View detailed analytics"
            className="hover:bg-primary/10 hover:text-primary hover:scale-110 transition-all duration-200"
          >
            <Icon name="BarChart3" size={20} />
          </Button>
        </div>
        
        <ProgressIndicator
          streakDays={currentStreak}
          versesMemorized={stats?.weeklyProgress}
          weeklyGoal={stats?.weeklyGoal}
          variant="detailed"
        />
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {progressCards?.map((card) => (
          <div
            key={card?.id}
            className="group/card bg-gradient-to-br from-card to-card/80 rounded-xl p-6 shadow-lg border border-border hover:shadow-xl hover:border-primary/30 hover:scale-[1.04] transition-all duration-300 ease-out flex flex-col h-40 cursor-default"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`flex items-center justify-center w-12 h-12 ${card?.bgColor} rounded-xl group-hover/card:scale-110 group-hover/card:shadow-md transition-all duration-300`}>
                <Icon name={card?.icon} size={20} color={card?.color} />
              </div>
              <Icon name="TrendingUp" size={16} className="text-muted-foreground group-hover/card:text-primary transition-colors duration-300" />
            </div>
            
            <div className="flex-1 flex flex-col justify-between">
              <div className="flex items-baseline space-x-1 mb-2">
                <span className="text-3xl font-mono font-bold text-foreground group-hover/card:text-primary transition-colors duration-300">
                  {card?.value}
                </span>
                <span className="text-lg-custom font-caption text-muted-foreground">
                  {card?.unit}
                </span>
              </div>
              <div className="mt-auto">
                <p className="text-lg-custom font-caption text-muted-foreground font-medium mb-1">
                  {card?.title}
                </p>
                <p className="text-sm text-muted-foreground">
                  {card?.description}
                </p>
                {card?.subtitle && (
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    {card?.subtitle}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Achievement Badges */}
      <div className="group bg-gradient-to-br from-card via-card to-accent/5 rounded-xl p-6 shadow-lg border border-border hover:shadow-xl hover:border-accent/30 hover:scale-[1.005] transition-all duration-300 ease-out">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl-custom font-heading font-bold text-foreground group-hover:text-primary transition-colors duration-300">Recent Achievements</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="Award"
            iconPosition="left"
            className="hover:bg-primary/10 hover:text-primary hover:scale-[1.03] transition-all duration-200"
          >
            View All
          </Button>
        </div>
        
        <div className="flex items-stretch gap-4 overflow-x-auto pb-2">
          {achievements?.map((achievement) => (
            <div
              key={achievement?.id}
              className="group/badge flex-shrink-0 flex flex-col items-center p-4 bg-gradient-to-br from-background/50 to-background/80 rounded-xl border border-border/50 hover:border-accent/40 hover:shadow-md hover:scale-[1.06] min-w-24 h-28 transition-all duration-300 ease-out cursor-default"
            >
              <div className={`flex items-center justify-center w-12 h-12 ${achievement?.bgColor} rounded-xl mb-2 group-hover/badge:scale-110 transition-transform duration-300`}>
                <Icon name={achievement?.icon} size={20} color={achievement?.color} />
              </div>
              <div className="text-center mt-auto">
                <p className="text-sm font-caption font-medium text-foreground mb-1">
                  {achievement?.title}
                </p>
                <p className="text-xs text-muted-foreground">
                  {achievement?.date}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressSummary;